import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import lodash from 'lodash'
import { justForSideEffect, justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  ApproveDisapprovePayload,
  CancelQuantityItemPayload,
  CreateInventoryPayload,
  DeleteBadgePayload,
  GetInventoryPayload,
  GetInventoryProductDetailsPayload,
  GetPositionPayload,
  IdBadgePayload,
  IdPayload,
  IncrementDecrementPayload,
  InventoriesToExecutePayload,
  InventoryList,
  InventoryProductDetails,
  InventoryState,
  InventoryStatus,
  ItemStatus,
  ResetAdjustmentApprovalPayload,
  SavePositionDamagePayload,
  SavePositionPayload,
  SetAdjustmentApproval,
  SetBadge,
  SetDataToExecuteInventory,
  SetDataPosition,
  SetInventoriesToExecute,
  SetInventoryDetails,
  SetInventoryList,
  SetInventoryStatus,
  SetSkusList,
  UnlinkOperatorPayload,
  ValidateBadgeExecuteInventoryPayload,
  ValidateBadgePayload,
  ValidateSkusPayload,
  ConfigsToExecuteInventory,
} from './InventoryTypes'

export const initialState: InventoryState = {
  inventoryList: [],
  inventoryDetails: {
    id: 0,
    countType: '',
    createdAt: new Date(''),
    creatorUserName: '',
    inventoryName: '',
    products: [],
    inventoryStatus: InventoryStatus.CREATED,
  },
  inventoryProductDetails: null,
  badge: [],
  skusList: [],
  configsToExecuteInventory: undefined,
  inventoriesToExecute: [],
  dataToExecuteInventory: {
    badge: '',
    floor: 0,
    addressesTypes: [],
  },
  dataPosition: {
    idPosition: 0,
    positionAddress: '',
    operatorName: '',
    productDescription: '',
    expirationDateBatches: [],
    countType: '',
    enabledThirdCount: false,
    openCount: false,
    tryAgain: false,
    currentTotal: 0,
    currentDamage: 0,
    afterFinishPosition: false,
  },
  isThirdCount: false,
  goToPosition: false,
  goToTableInventories: false,
  clickExecuteInventory: false,
  needGetPosition: false,
}

const getInventory = justForSideEffectWithPayload<GetInventoryPayload>()
const createInventory = justForSideEffectWithPayload<CreateInventoryPayload>()
const validateSkus = justForSideEffectWithPayload<ValidateSkusPayload>()
const validateAndAddBadge = justForSideEffectWithPayload<ValidateBadgePayload>()
const deleteBadge = justForSideEffectWithPayload<DeleteBadgePayload>()
const getBadges = justForSideEffect()
const getInventoryDetail = justForSideEffectWithPayload<IdPayload>()
const getInventoryProductDetails = justForSideEffectWithPayload<GetInventoryProductDetailsPayload>()
const cancelInventory = justForSideEffectWithPayload<IdPayload>()
const getInventoriesToExecute = justForSideEffectWithPayload<InventoriesToExecutePayload>()
const getConfigsToExecuteInventory = justForSideEffect()
const validateBadgeToExecuteInventory = justForSideEffectWithPayload<ValidateBadgeExecuteInventoryPayload>()
const finishInventory = justForSideEffectWithPayload<IdPayload>()
const incrementDecrementInventory = justForSideEffectWithPayload<IncrementDecrementPayload>()
const approveAdjustment = justForSideEffectWithPayload<ApproveDisapprovePayload>()
const rejectAdjustment = justForSideEffectWithPayload<ApproveDisapprovePayload>()
const resetAdjustmentApproval = justForSideEffectWithPayload<ResetAdjustmentApprovalPayload>()
const getPosition = justForSideEffectWithPayload<GetPositionPayload>()
const registerStartPosition = justForSideEffectWithPayload<IdBadgePayload>()
const savePosition = justForSideEffectWithPayload<SavePositionPayload>()
const savePositionDamage = justForSideEffectWithPayload<SavePositionDamagePayload>()
const cancelQuantityItem = justForSideEffectWithPayload<CancelQuantityItemPayload>()
const finishPosition = justForSideEffectWithPayload<IdBadgePayload>()
const unlinkOperator = justForSideEffectWithPayload<UnlinkOperatorPayload>()

const setInventoryList = (
  state: InventoryState,
  action: PayloadAction<SetInventoryList>,
): InventoryState => ({
  ...state,
  inventoryList: action.payload.inventoryList,
})

const setBadge = (state: InventoryState, action: PayloadAction<SetBadge>): InventoryState => ({
  ...state,
  badge: action.payload.badge,
})

const setBadgeAdd = (state: InventoryState, action: PayloadAction<SetBadge>): InventoryState => ({
  ...state,
  badge: [...state.badge, ...action.payload.badge],
})

const setSkusList = (
  state: InventoryState,
  action: PayloadAction<SetSkusList>,
): InventoryState => ({
  ...state,
  skusList: [...state.skusList, ...action.payload.skusList],
})

const setSkusListDelete = (
  state: InventoryState,
  action: PayloadAction<SetSkusList>,
): InventoryState => ({
  ...state,
  skusList: action.payload.skusList,
})

const setInventoryDetail = (
  state: InventoryState,
  action: PayloadAction<SetInventoryDetails>,
): InventoryState => ({
  ...state,
  inventoryDetails: action.payload.inventoryDetails,
})

const setInventoryProductDetails = (
  state: InventoryState,
  action: PayloadAction<InventoryProductDetails>,
): InventoryState => ({
  ...state,
  inventoryProductDetails: action.payload,
})

const setInventoryStatus = (
  state: InventoryState,
  action: PayloadAction<SetInventoryStatus>,
): InventoryState => {
  const inventoryList = lodash.cloneDeep(state.inventoryList)

  const indexToCanceled = inventoryList.filter((inventory: InventoryList) => inventory.id === action.payload.id)

  indexToCanceled[0].currentStatus = action.payload.currentStatus

  return {
    ...state,
    inventoryList,
  }
}

const setConfigsToExecuteInventory = (
  state: InventoryState,
  action: PayloadAction<ConfigsToExecuteInventory>,
): InventoryState => ({
  ...state,
  configsToExecuteInventory: action.payload,
})

const setInventoriesToExecute = (
  state: InventoryState,
  action: PayloadAction<SetInventoriesToExecute>,
): InventoryState => ({
  ...state,
  inventoriesToExecute: action.payload.inventoriesToExecute,
})

const setDataPosition = (
  state: InventoryState,
  action: PayloadAction<SetDataPosition>,
): InventoryState => ({
  ...state,
  dataPosition: action.payload.dataPosition,
})

const setDataToExecuteInventory = (state: InventoryState, action: PayloadAction<SetDataToExecuteInventory>): InventoryState => ({
  ...state,
  dataToExecuteInventory: {
    ...state.dataToExecuteInventory,
    ...action.payload,
  },
})

const setApprovalAdjustment = (state: InventoryState, action: PayloadAction<SetAdjustmentApproval>) => {
  const currentProducts = lodash.cloneDeep(state.inventoryDetails.products)
  const productToUpdate = currentProducts.find((product) => product.id === action.payload.idProductInventory)

  if (productToUpdate) {
    productToUpdate.adjustmentApproval = action.payload.approvalStatus
    productToUpdate.status = ItemStatus.ADJUSTMENT_ANALYSIS
  }

  return {
    ...state,
    inventoryDetails: {
      ...state.inventoryDetails,
      products: currentProducts,
    },
  }
}

const setThirdCount = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, isThirdCount: action.payload })

const setAfterFinishPosition = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, dataPosition: { ...state.dataPosition, afterFinishPosition: action.payload } })

const setGoToPosition = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, goToPosition: action.payload })

const setGoToTableInventories = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, goToTableInventories: action.payload })

const setClickExecuteInventory = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, clickExecuteInventory: action.payload })

const setNeedGetPosition = (state: InventoryState, action: PayloadAction<boolean>) => ({ ...state, needGetPosition: action.payload })

const clearState = (state: InventoryState): InventoryState => ({
  ...initialState,
  configsToExecuteInventory: state.configsToExecuteInventory,
})

const clearAllState = (): InventoryState => (initialState)

const slice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    getInventory,
    setInventoryList,
    createInventory,
    validateSkus,
    validateAndAddBadge,
    setBadge,
    setSkusList,
    clearState,
    clearAllState,
    deleteBadge,
    setSkusListDelete,
    getBadges,
    setBadgeAdd,
    getInventoryDetail,
    getInventoryProductDetails,
    setInventoryDetail,
    setInventoryProductDetails,
    cancelInventory,
    setInventoryStatus,
    getInventoriesToExecute,
    setInventoriesToExecute,
    getConfigsToExecuteInventory,
    setConfigsToExecuteInventory,
    validateBadgeToExecuteInventory,
    setDataToExecuteInventory,
    finishInventory,
    incrementDecrementInventory,
    approveAdjustment,
    rejectAdjustment,
    resetAdjustmentApproval,
    setDataPosition,
    getPosition,
    registerStartPosition,
    savePosition,
    savePositionDamage,
    cancelQuantityItem,
    finishPosition,
    setThirdCount,
    unlinkOperator,
    setAfterFinishPosition,
    setGoToPosition,
    setGoToTableInventories,
    setClickExecuteInventory,
    setNeedGetPosition,
    setApprovalAdjustment,
  },
})

export default slice.reducer

export const { actions } = slice
